import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Loader2, Sparkles } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import ImageStage from '@/components/ui/image-stage';
import { Modal } from '@/components/ui/modal';
import { SimpleSelect } from '@/components/ui/simple-select';
import type { SimpleSelectOption } from '@/components/ui/simple-select';
import { Typography } from '@/components/ui/typography';
import type {
  ImageAssetType,
  ImageModelCaches
} from '@/components/ui/carousel/carousel.types';
import { cn } from '@/lib/utils';
import { Variants } from '@/types/common';
import { RootState } from '../../../store/store';
import { showAlert } from '../../../slice/AlertSlice';
import { addStaged, updateStaged } from '../slice/ImageAssetSlice';
import {
  useCommitVirtualStagingMutation,
  useGetVirtualStagingOptionsQuery,
  useLazyGetVirtualStagingByAssetQuery
} from '../services/assetApi';
import BeforeAfterPreview, {
  PREVIEW_STAGE_CLASS,
  PREVIEW_STAGE_STYLE
} from './BeforeAfterPreview';
import {
  FALLBACK_STAGING_OPTIONS,
  roomTypeFromCaption,
  roomTypeLabel,
  styleLabel
} from './stageable';
import { STAGED_CAPTION_PREFIX, toStagedImageAsset } from './toStagedImageAsset';
import { useStagingRenders } from './useStagingRender';

type PhotoStagingStatus = 'pending' | 'staged' | 'skipped';

type PhotoDraft = { roomType: string; style: string };

// Restage lifecycle around the by-asset lookup: 'unavailable' = the staged
// photo has no render record (it was UPLOADED, not AI-generated — a real
// state, not a transport failure); 'failed' = the lookup itself errored.
type RestageStatus = 'loading' | 'ready' | 'unavailable' | 'failed';

/** Backend hard cap (asset-service MAX_REGENERATIONS); slots are consumed server-side. */
const REGEN_MAX = 10;

const isNotFound = (error: unknown): boolean =>
  typeof error === 'object' &&
  error !== null &&
  (error as { status?: unknown }).status === 404;

type StagingModalProps = {
  photos: ImageAssetType[];
  onClose: () => void;
};

const previewUrl = (image: ImageAssetType) =>
  image.caches?.['1920w'] ||
  image.caches?.fullSize ||
  image.caches?.fullsize ||
  image.caches?.['830w'];

const stagedResultUrl = (resultCaches: Record<string, string>) =>
  resultCaches['1920w'] ??
  resultCaches['fullSize'] ??
  Object.values(resultCaches)[0];

const statusSuffix = (status: PhotoStagingStatus): string => {
  if (status === 'staged') return ' · staged';
  if (status === 'skipped') return ' · skipped';
  return '';
};

/**
 * Internal-only Stage with AI flow (DV-2252): setup → generating → result per
 * photo, driving the LIVE generate endpoints. Approve commits the render to a
 * real Assets doc (blocking) and then inserts it into the working set.
 * Opened on a single already-staged photo it enters RESTAGE mode: the render
 * record is recovered via the by-asset endpoint, Regenerate re-runs VSAI, and
 * Save Changes re-commits — the backend replaces the committed asset in place.
 * Ported from the design prototype hifi/ma-staging.jsx.
 */
const StagingModal = ({ photos, onClose }: StagingModalProps) => {
  const dispatch = useDispatch();
  const isBatch = photos.length > 1;
  const [index, setIndex] = useState(0);
  const [photoStatus, setPhotoStatus] = useState<PhotoStagingStatus[]>(() =>
    photos.map(() => 'pending')
  );
  const { data: liveOptions } = useGetVirtualStagingOptionsQuery();
  const options = liveOptions ?? FALLBACK_STAGING_OPTIONS;
  const current = photos[index];
  const { stateFor, startRender, regenerate, seedRender } =
    useStagingRenders();
  const { phase: rawPhase, render, errorMessage } = stateFor(current.photoUUID);
  const [commitStaging, { isLoading: isCommitting }] =
    useCommitVirtualStagingMutation();

  // Restage mode (DV-2252 regenerate-after-staged): batch staging never
  // includes staged photos, so a staged photo can only arrive alone. Keyed on
  // `stagedFromAssetUUID`, NOT `staged` — `staged` also covers a plain upload
  // filed into the Virtual Staging tab, which has no render record to restage
  // (DV-2826; the hover affordance in AssetsImageCard uses the same signal so
  // this branch is normally reached only for a genuine AI-staged photo, but
  // the check stays independent here rather than trusted from the caller).
  const restage = !isBatch && Boolean(photos[0].stagedFromAssetUUID);
  const [fetchByAsset] = useLazyGetVirtualStagingByAssetQuery();
  const [restageStatus, setRestageStatus] = useState<RestageStatus>(
    restage ? 'loading' : 'ready'
  );
  // Save Changes stays disarmed until a regenerate actually ran — the
  // committed photo is already saved, so re-committing it would be a no-op.
  const [hasRegenerated, setHasRegenerated] = useState(false);
  const imageAssets = useSelector(
    (state: RootState) => state.imageAsset.imageAssets
  );

  // Read via a ref (not a dependency) so a late-resolving options query
  // can't clobber a style the user already picked — see the effect below.
  const defaultStyleRef = useRef(options.defaultStyle);
  defaultStyleRef.current = options.defaultStyle;

  // Per-photo controls: each photo keeps its own room type/style, so
  // navigating the batch never discards a pick (render lifecycles are
  // likewise per-photo, in useStagingRenders). Initialized lazily on first
  // visit; deliberately NOT re-derived when options resolve: if the
  // staging-options query resolves after the user has already changed the
  // Style select, re-running would silently overwrite their choice.
  const [drafts, setDrafts] = useState<Record<string, PhotoDraft>>({});
  useEffect(() => {
    // Restage seeds its draft from the fetched render record instead (below).
    if (restage) return;
    const photo = photos[index];
    setDrafts((prev) =>
      prev[photo.photoUUID]
        ? prev
        : {
            ...prev,
            [photo.photoUUID]: {
              roomType: roomTypeFromCaption(photo.caption),
              style: defaultStyleRef.current
            }
          }
    );
  }, [index, photos, restage]);

  // Recover the render record for a photo committed in an earlier session,
  // then drive the normal regenerate → poll → commit loop from it.
  useEffect(() => {
    if (!restage) return;
    let cancelled = false;
    (async () => {
      try {
        const record = await fetchByAsset(photos[0].assetUUID).unwrap();
        if (cancelled) return;
        seedRender(photos[0].photoUUID, record);
        // Pre-fill the controls from the COMMITTED staging carried on the
        // staged photo itself (stagedRoomType/stagedStyle) — NOT the record,
        // whose roomType/style drift to a discarded regeneration (DV-2797).
        // Fall back to the record for a legacy staged asset that lacks the
        // fields (correct unless it has drifted — the rare legacy-AND-discard
        // intersection). record.roomType/style are non-nullable + backend-
        // validated, so no further fallback is needed.
        setDrafts((prev) =>
          prev[photos[0].photoUUID]
            ? prev
            : {
                ...prev,
                [photos[0].photoUUID]: {
                  roomType: photos[0].stagedRoomType ?? record.roomType,
                  style: photos[0].stagedStyle ?? record.style
                }
              }
        );
        setRestageStatus('ready');
      } catch (error) {
        if (!cancelled) {
          setRestageStatus(isNotFound(error) ? 'unavailable' : 'failed');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [restage, photos, fetchByAsset, seedRender]);

  // Before image for the restage compare view: the render's assetUUID is the
  // ORIGINAL asset the staging ran on. `originalAsset` is undefined when it is
  // no longer in the working set (e.g. trashed after staging) — tracked as an
  // explicit flag so the compare view keys on "is there a real Before?" rather
  // than a reference-identity check coupled to the `?? current` fallback below.
  const originalAsset = restage
    ? imageAssets.find((img) => img.assetUUID === render?.assetUUID)
    : undefined;
  const originalPresent = Boolean(originalAsset);
  const restageOriginal = originalAsset ?? current;

  const draft = drafts[current.photoUUID] ?? {
    roomType: roomTypeFromCaption(current.caption),
    style: defaultStyleRef.current
  };
  const { roomType, style } = draft;
  const patchDraft = (patch: Partial<PhotoDraft>) =>
    setDrafts((prev) => ({
      ...prev,
      [current.photoUUID]: { ...draft, ...patch }
    }));

  const restageLoading = restage && restageStatus === 'loading';
  const restageBlocked =
    restage &&
    (restageStatus === 'unavailable' || restageStatus === 'failed');
  const regenUsed = render?.regenerationCount ?? 0;

  // DV-2797: a regeneration the user DISCARDED drifts the render record —
  // resultCaches / roomType / style / status all move to the discarded
  // variation, and Discard only closes the modal (it never reverts the record).
  // So in restage, until the user regenerates again in THIS session, the
  // committed baseline is the staged photo itself, not the record. A single
  // decision (`useCommitted`) drives BOTH the image and its labels so they can
  // never describe different renders.
  //
  // BAND-AID (CLAUDE.md): this compensates in the view for the record drifting
  // server-side; the root fix — Discard reconciling the record, or a
  // committed-variation-aware by-asset fetch — is deferred to DV-2813.
  // regenerationCount is deliberately NOT compensated: by product it aggregates
  // every re-render for the image, discarded ones included, so it's correct.
  const showsCommittedBaseline = restage && !hasRegenerated;
  const committedImageUrl = restage ? previewUrl(current) : undefined;
  const renderResultUrl = render?.resultCaches
    ? stagedResultUrl(render.resultCaches)
    : undefined;
  // Prefer the committed staged image; fall back to the record's result only
  // when the committed caches are absent (legacy staged asset without standard
  // sizes), so the preview never blanks. Labels follow the SAME choice.
  const useCommitted = showsCommittedBaseline && Boolean(committedImageUrl);
  const stagedAfterUrl = useCommitted ? committedImageUrl : renderResultUrl;
  // Committed labels come from the staged photo, then the record (both static)
  // — NOT the draft (`roomType`/`style`), which drives the live selects and
  // would relabel the still-committed image as the user changes a select
  // without regenerating. DV-2797 review.
  const displayRoomType = useCommitted
    ? current.stagedRoomType ?? render?.roomType ?? roomType
    : render?.roomType ?? roomType;
  const displayStyle = useCommitted
    ? current.stagedStyle ?? render?.style ?? style
    : render?.style ?? style;

  // No original in the working set → there is no real "Before"; show a single
  // image instead of a compare that would mislabel the staged image as the
  // original room — both before AND after an in-session regenerate.
  const compareSuppressed = restage && !originalPresent;

  // While showing the committed baseline, present it as the committed 'done'
  // view regardless of the drifted record's phase — including a discarded
  // FAILED regen whose record persists status ERROR. A discard stays discarded.
  //
  // NOT for a still-RENDERING record though: if the user discarded a
  // regeneration mid-render and reopens, the VSAI variation is still rendering,
  // so keep the live "Generating…" state instead of a 'done' view that would
  // re-arm Regenerate — clicking it would fire a SECOND paid VSAI call on an
  // already-rendering record (useStagingRenders' isRequesting guard doesn't
  // cover a seeded poll). The result stays discarded once it completes. DV-2797.
  const phase =
    showsCommittedBaseline &&
    !restageLoading &&
    stagedAfterUrl &&
    rawPhase !== 'generating'
      ? 'done'
      : rawPhase;

  const roomOptions: SimpleSelectOption[] = options.roomTypes.map((value) => ({
    label: roomTypeLabel(value),
    value
  }));
  const styleOptions: SimpleSelectOption[] = options.styles.map((value) => ({
    label: styleLabel(value),
    value
  }));

  const generate = () =>
    void startRender(current.photoUUID, current.assetUUID, {
      roomType,
      style
    });

  const markStatus = (
    statuses: PhotoStagingStatus[],
    status: PhotoStagingStatus
  ) => {
    const next = [...statuses];
    next[index] = status;
    setPhotoStatus(next);
    return next;
  };

  const finish = (statuses: PhotoStagingStatus[]) => {
    onClose();
    const stagedCount = statuses.filter((status) => status === 'staged').length;
    dispatch(
      showAlert({
        type: Variants.SUCCESS,
        message: `${stagedCount} of ${photos.length} ${
          photos.length === 1 ? 'photo' : 'photos'
        } staged`
      })
    );
  };

  // Move to the next photo still awaiting a decision (wrapping past staged/
  // skipped ones); only finish when none remain — matches the design
  // prototype's batch flow, and pairs with per-photo state: a photo the user
  // navigated away from mid-render is still pending, not lost.
  const advance = (statuses: PhotoStagingStatus[]) => {
    for (let step = 1; step <= photos.length; step++) {
      const nextIndex = (index + step) % photos.length;
      if (statuses[nextIndex] === 'pending') {
        setIndex(nextIndex);
        return;
      }
    }
    finish(statuses);
  };

  const skip = () => advance(markStatus(photoStatus, 'skipped'));

  const handleThumbKeyDown = (
    event: KeyboardEvent<HTMLButtonElement>,
    i: number
  ) => {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
    event.preventDefault();
    const direction = event.key === 'ArrowRight' ? 1 : -1;
    const next = (i + direction + photos.length) % photos.length;
    setIndex(next);
    const tabs =
      event.currentTarget.parentElement?.querySelectorAll('button[role="tab"]');
    (tabs?.[next] as HTMLButtonElement | undefined)?.focus();
  };

  // Regenerate re-runs VSAI against the existing render record; in restage
  // mode it also arms Save Changes (there is now something new to save).
  const handleRegenerate = () => {
    if (restage) setHasRegenerated(true);
    void regenerate(current.photoUUID, { roomType, style });
  };

  const approve = async () => {
    if (!render?.resultCaches || isCommitting) return;
    if (restage) {
      // Re-commit: the backend replaces the committed asset IN PLACE (same
      // assetUUID/photoUUID, new image). No body fields needed — the render
      // record carries everything, and omitting caption preserves it.
      let committed;
      try {
        committed = await commitStaging({
          renderRecordId: render.renderRecordId,
          request: {}
        }).unwrap();
      } catch {
        dispatch(
          showAlert({
            type: Variants.ERROR,
            message: 'Could not save the regenerated photo. Please try again.'
          })
        );
        return;
      }
      dispatch(
        updateStaged({
          photoUUID: current.photoUUID,
          caches: {
            ...((committed.caches ?? render.resultCaches) as ImageModelCaches)
          },
          stagedRoomType: render.roomType,
          stagedStyle: render.style
        })
      );
      onClose();
      dispatch(
        showAlert({
          type: Variants.SUCCESS,
          message: 'Virtually staged photo updated'
        })
      );
      return;
    }
    // Block on the commit: only insert into the working set once the render is
    // persisted as a real Asset, so no optimistic/fake-id item ever exists.
    let committed;
    try {
      committed = await commitStaging({
        renderRecordId: render.renderRecordId,
        request: {
          originalAssetUUID: render.assetUUID,
          ...(current.photosetUUID
            ? { photosetUUID: current.photosetUUID }
            : {}),
          caption: `${STAGED_CAPTION_PREFIX}${current.caption ?? ''}`
        }
      }).unwrap();
    } catch {
      dispatch(
        showAlert({
          type: Variants.ERROR,
          message: 'Could not save the staged photo. Please try again.'
        })
      );
      return;
    }

    const staged = toStagedImageAsset(committed, current, render);
    dispatch(addStaged({ originalPhotoUUID: current.photoUUID, staged }));
    const statuses = markStatus(photoStatus, 'staged');
    if (isBatch) {
      advance(statuses);
      return;
    }
    onClose();
    dispatch(
      showAlert({
        type: Variants.SUCCESS,
        message: 'Virtually staged photo added alongside the original'
      })
    );
  };

  const [confirmDiscard, setConfirmDiscard] = useState(false);

  // Hard warn (from the design prototype): a generated result is a REAL, paid
  // VSAI render — closing before Approve throws it away. Restage differs: the
  // committed photo is already saved, so only an unsaved regeneration counts.
  const hasUnapprovedResult = restage
    ? hasRegenerated
    : photos.some((p, i) => {
        if (photoStatus[i] === 'staged') return false;
        const { phase: photoPhase } = stateFor(p.photoUUID);
        return photoPhase === 'generating' || photoPhase === 'done';
      });

  const requestClose = () => {
    if (hasUnapprovedResult) setConfirmDiscard(true);
    else onClose();
  };

  const secondaryAction = isBatch ? (
    <Button variant="ghost" onClick={skip}>
      {phase === 'done' ? 'Skip' : 'Skip This Photo'}
    </Button>
  ) : (
    <Button variant="ghost" onClick={requestClose}>
      Cancel
    </Button>
  );

  // Restage that can't proceed (loading / no render record / lookup failure)
  // offers Cancel only — there is nothing to generate or save.
  const footer =
    restage && restageStatus !== 'ready' ? (
      <Button variant="ghost" onClick={onClose}>
        Cancel
      </Button>
    ) : (
      <>
        {phase === 'generating' ? (
          <Button variant="ghost" onClick={requestClose}>
            Cancel
          </Button>
        ) : (
          secondaryAction
        )}
        {phase === 'idle' && (
          <Button onClick={generate}>
            <Sparkles className="h-4 w-4" aria-hidden /> Generate Staging
          </Button>
        )}
        {phase === 'generating' && (
          <Button disabled>
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Generating…
          </Button>
        )}
        {phase === 'done' && (
          <Button
            onClick={() => void approve()}
            disabled={isCommitting || (restage && !hasRegenerated)}
          >
            {isCommitting && (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            )}
            {restage
              ? 'Save Changes'
              : isBatch
                ? 'Approve & Next'
                : 'Approve and Add to Photoset'}
          </Button>
        )}
        {phase === 'error' && (
          <Button onClick={restage ? handleRegenerate : generate}>
            <Sparkles className="h-4 w-4" aria-hidden /> Try Again
          </Button>
        )}
      </>
    );

  return (
    <>
    <Modal
      open
      onOpenChange={(open) => !open && requestClose()}
      size="viewer"
      title={
        <span className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-primary" aria-hidden />
          Stage with AI
          <Badge variant="secondary">Internal</Badge>
          {isBatch && (
            <span className="text-sm font-normal text-muted-foreground">
              Photo {index + 1} of {photos.length}
            </span>
          )}
        </span>
      }
      footer={footer}
    >
      <div className="flex flex-col gap-4">
        <div
          role={isBatch ? 'tabpanel' : undefined}
          aria-labelledby={isBatch ? `staging-thumb-${index}` : undefined}
        >
          {phase === 'done' && stagedAfterUrl && !compareSuppressed ? (
            <BeforeAfterPreview
              original={restageOriginal}
              stagedUrl={stagedAfterUrl}
              roomType={displayRoomType}
              style={displayStyle}
            />
          ) : phase === 'done' && stagedAfterUrl && compareSuppressed ? (
            // Original gone → single image (the committed baseline, or the fresh
            // result after an in-session regenerate), never a mislabeled compare.
            <ImageStage
              src={stagedAfterUrl}
              alt={current.caption ?? 'Photo'}
              placeholderSrc={current.caches?.['300w']}
              className={PREVIEW_STAGE_CLASS}
              style={PREVIEW_STAGE_STYLE}
            />
          ) : (
            <ImageStage
              src={previewUrl(current)}
              alt={current.caption ?? 'Photo'}
              // Already painted by the grid card the user just clicked.
              placeholderSrc={current.caches?.['300w']}
              className={PREVIEW_STAGE_CLASS}
              style={PREVIEW_STAGE_STYLE}
              imageClassName={cn(
                (phase === 'generating' || restageLoading) &&
                  'blur-[3px] brightness-75'
              )}
            >
              {(phase === 'generating' || restageLoading) && (
                <div className="absolute inset-0 flex items-center justify-center gap-2.5 text-sm font-medium text-white">
                  <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
                  {restageLoading
                    ? 'Loading staging details…'
                    : `Staging ${roomTypeLabel(roomType).toLowerCase()}…`}
                </div>
              )}
            </ImageStage>
          )}
        </div>

        {isBatch && (
          <div
            role="tablist"
            aria-label="Photos to stage"
            // `w-0 min-w-full` keeps the strip from widening the (shrink-to-fit)
            // viewer modal: a large batch's max-content width would otherwise
            // stretch the dialog toward 95vw and re-open the gutters around the
            // preview. Width 0 removes it from the intrinsic-width calculation;
            // min-width fills whatever the preview settled on, and it scrolls.
            className="flex w-0 min-w-full gap-2 overflow-x-auto py-1"
          >
            {photos.map((batchPhoto, i) => (
              <button
                key={batchPhoto.photoUUID}
                id={`staging-thumb-${i}`}
                type="button"
                role="tab"
                aria-selected={i === index}
                aria-label={`${
                  batchPhoto.caption ?? `Photo ${i + 1}`
                }${statusSuffix(photoStatus[i])}`}
                tabIndex={i === index ? 0 : -1}
                onClick={() => setIndex(i)}
                onKeyDown={(event) => handleThumbKeyDown(event, i)}
                className={cn(
                  'relative h-12 w-16 shrink-0 overflow-hidden rounded border-2',
                  i === index ? 'border-primary' : 'border-transparent',
                  photoStatus[i] === 'skipped' && 'opacity-60 grayscale'
                )}
              >
                <img
                  src={
                    batchPhoto.caches?.['300w'] || batchPhoto.caches?.['830w']
                  }
                  alt=""
                  className="h-full w-full object-cover"
                />
                <span className="absolute left-0.5 top-0.5 rounded bg-black/60 px-1 text-[10px] font-semibold text-white">
                  {i + 1}
                </span>
                {photoStatus[i] === 'staged' && (
                  <span className="absolute bottom-0.5 right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-primary-foreground">
                    <Sparkles className="h-2.5 w-2.5" aria-hidden />
                  </span>
                )}
                {photoStatus[i] === 'skipped' && (
                  <span className="absolute bottom-0.5 right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-muted-foreground text-[10px] text-white">
                    —
                  </span>
                )}
              </button>
            ))}
          </div>
        )}

        {restageBlocked && (
          <Typography as="p" variant="body" className="text-destructive">
            {restageStatus === 'unavailable'
              ? "This staged photo can't be regenerated — it was uploaded rather than generated with AI staging."
              : 'Could not load staging details. Close and try again.'}
          </Typography>
        )}

        {!restageBlocked && (
        <div className="grid grid-cols-2 gap-4">
          <SimpleSelect
            label="Room Type"
            options={roomOptions}
            value={
              roomOptions.find((option) => option.value === roomType) ?? null
            }
            setFieldValue={(_: string, value: unknown) =>
              patchDraft({ roomType: value as string })
            }
            disabled={phase === 'generating'}
            field="stagingRoomType"
            controlId="stagingRoomType"
            name="stagingRoomType"
            md=""
            placeholder="Room Type"
          />
          <SimpleSelect
            label="Staging Style"
            options={styleOptions}
            value={
              styleOptions.find((option) => option.value === style) ?? null
            }
            setFieldValue={(_: string, value: unknown) =>
              patchDraft({ style: value as string })
            }
            disabled={phase === 'generating'}
            field="stagingStyle"
            controlId="stagingStyle"
            name="stagingStyle"
            md=""
            placeholder="Staging Style"
          />
        </div>
        )}

        {!restageBlocked && phase === 'error' && (
          <Typography as="p" variant="body" className="text-destructive">
            {errorMessage ??
              'Staging failed. Adjust the room type or style and try again.'}
          </Typography>
        )}
        {!restageBlocked && phase === 'done' ? (
          <div className="flex items-center gap-3">
            <Typography
              as="p"
              variant="body"
              className="flex-1 text-muted-foreground"
            >
              {regenUsed >= REGEN_MAX
                ? 'Regeneration limit reached for this photo.'
                : `Adjust room type or style and regenerate to change the result (${regenUsed} of ${REGEN_MAX} regenerations used).`}
            </Typography>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRegenerate}
              disabled={regenUsed >= REGEN_MAX}
            >
              Regenerate
            </Button>
          </div>
        ) : (
          !restageBlocked &&
          phase !== 'error' && (
            <Typography as="p" variant="body" className="text-muted-foreground">
              Pre-filled from the caption — change the room type if it&apos;s
              wrong before generating.
            </Typography>
          )
        )}
      </div>
    </Modal>

    <Modal
      open={confirmDiscard}
      onOpenChange={(open) => !open && setConfirmDiscard(false)}
      size="sm"
      title="Discard Staged Image?"
      description="A generated staging result hasn't been approved yet. Closing discards it, and generated results can't be recovered."
      footer={
        <>
          <Button
            variant="outline"
            onClick={() => setConfirmDiscard(false)}
            autoFocus
          >
            Keep Editing
          </Button>
          <Button
            variant="destructive"
            onClick={() => {
              setConfirmDiscard(false);
              onClose();
            }}
          >
            Discard Staged Image
          </Button>
        </>
      }
    />
    </>
  );
};

export default StagingModal;
